import React, { useState } from 'react';
import { X, Check, Loader2 } from 'lucide-react';
import { formatDateTimeMdY } from '@/utils/dateFormat';
import { adminDialogPanel, adminOverlay } from '@/components/admin/adminUi';

export default function SchedulePendingApprovalModal({
  open,
  request,
  onClose,
  onApprove,
  onReject,
  processing = false,
}) {
  const [rejectNotes, setRejectNotes] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  if (!open || !request) return null;

  const handleClose = () => {
    setRejectNotes('');
    setShowRejectForm(false);
    onClose();
  };

  const handleReject = () => {
    onReject(request, rejectNotes.trim());
    setRejectNotes('');
    setShowRejectForm(false);
  };

  const preferredStart = formatDateTimeMdY(request.preferred_start_at, '—');

  return (
    <div className={adminOverlay()}>
      <div
        className={`${adminDialogPanel('max-w-lg')} shadow-2xl`}
        data-testid="schedule-pending-approval-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h2 className="text-base font-['Unbounded'] font-semibold text-white">
            Pending Request Details
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="p-1.5 hover:bg-white/10 text-white/60 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <p className="text-lg font-['Manrope'] font-medium text-white">
              {request.event_type_label || request.event_type}
            </p>
            <p className="text-xs font-['Manrope'] text-white/40 mt-1">
              {request.calendar_type === 'employee' ? 'Employee Calendar' : 'Service Calendar'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 text-xs font-['Manrope']">
            <div className="bg-white/[0.03] border border-white/10 p-3">
              <p className="text-white/40 mb-1">Customer / Lead</p>
              <p className="text-white">{request.customer_name}</p>
              {request.customer_phone && (
                <p className="text-white/50 mt-1">{request.customer_phone}</p>
              )}
            </div>
            <div className="bg-white/[0.03] border border-white/10 p-3">
              <p className="text-white/40 mb-1">Vehicle</p>
              <p className="text-white">{request.vehicle_info}</p>
            </div>
            <div className="bg-white/[0.03] border border-white/10 p-3">
              <p className="text-white/40 mb-1">Preferred Date & Time</p>
              <p className="text-white">{preferredStart}</p>
            </div>
            <div className="bg-white/[0.03] border border-white/10 p-3">
              <p className="text-white/40 mb-1">Duration</p>
              <p className="text-white">{request.duration_label || `${request.duration_minutes} min`}</p>
            </div>
          </div>

          {request.notes && (
            <div className="text-xs font-['Manrope'] border-t border-white/10 pt-3">
              <p className="text-white/40 mb-1">Notes</p>
              <p className="text-white/70">{request.notes}</p>
            </div>
          )}

          <div className="text-xs font-['Manrope'] text-white/40 space-y-1 border-t border-white/10 pt-3">
            <p>Requested by: {request.source_label || request.source}</p>
            {request.created_at && (
              <p>Submitted: {formatDateTimeMdY(request.created_at)}</p>
            )}
            {request.lead_id && (
              <p>Lead: {request.lead_id}{request.lead_type ? ` (${request.lead_type})` : ''}</p>
            )}
            {request.deal_id && (
              <p>Deal: {request.deal_label || request.deal_id}</p>
            )}
            {request.vehicle_id && <p>Vehicle ID: {request.vehicle_id}</p>}
            {request.service_request_id && (
              <p>Service Request: {request.service_request_id}</p>
            )}
          </div>

          {showRejectForm && (
            <div className="border-t border-white/10 pt-3">
              <label className="block text-xs font-['Manrope'] text-white/50 mb-1.5">
                Rejection reason (optional)
              </label>
              <textarea
                value={rejectNotes}
                onChange={(e) => setRejectNotes(e.target.value)}
                rows={3}
                className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2 font-['Manrope'] focus:outline-none focus:border-red-500/40"
                placeholder="Brief reason for rejection…"
              />
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 px-5 py-4 border-t border-white/10">
          {!showRejectForm ? (
            <>
              <button
                type="button"
                disabled={processing}
                onClick={() => onApprove(request)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-['Manrope']"
              >
                {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Approve
              </button>
              <button
                type="button"
                disabled={processing}
                onClick={() => setShowRejectForm(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600/40 hover:bg-red-500/60 disabled:opacity-50 text-white text-sm font-['Manrope']"
              >
                <X className="w-4 h-4" />
                Reject
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-['Manrope'] text-white/50 hover:text-white"
              >
                Close
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                disabled={processing}
                onClick={handleReject}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-sm font-['Manrope']"
              >
                {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                Confirm Reject
              </button>
              <button
                type="button"
                disabled={processing}
                onClick={() => { setShowRejectForm(false); setRejectNotes(''); }}
                className="px-4 py-2 text-sm font-['Manrope'] text-white/50 hover:text-white"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}